
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, TextField, Typography, Box, Tooltip, InputAdornment } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useClaimActions } from '../hooks/useApiHook';
import { backendDateFormat, fieldDateFormat } from '../../../constants';
import { regexConstants } from '../../../constants/regEx';
import { AttachementsUpload, ButtonLoader } from '../../../shared_elements/components';
import { isValidYearRange } from '../../../utils/helper';
import { numberWithCommas, roundAmount } from '../../../utils';
import { PageLoader } from '../../../shared_elements';
import { trackActivity } from '../../../utils/mixpanel';
import { formatBytes } from '../../../utils/fileUpload';
import { getStorage } from '../../../shared_elements/actionCreators';
import { setFileUploadDetails } from '../../../shared_elements/actions';
import { hasLlpEvent, hasNonLlpEvent } from '../utils';
const ClaimCRU = ({ params, shopVisit, updatedAsset, modeType, addEditClaim, onOpenAttachments, onToggleModal, getResponseBack, fileUpload, llpClaimTotal }) => {
  const isLlpEvent = hasLlpEvent(shopVisit);
  const isPrsv = shopVisit?.asset_type === 2 ? hasNonLlpEvent(shopVisit) : shopVisit?.prsv;
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { leaseBasicDetails, storageDetails } = useSelector((state) => state.leases);
  const [isLoading, setLoading] = useState({ skeletonLoader: false, pageLoader: false, buttonLoader: false, storage:false });
  const toggleLoader = (type, status) => { setLoading({ [type]: status }) }
  const { saveClaim, uploadFiles } = useClaimActions(params, toggleLoader);
  const [claim, setClaim] = useState(addEditClaim?.data)
  const [error, setError] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const claimPeriodEndDate = shopVisit?.claim_approval_period && moment(shopVisit?.shop_visit_end_date).add(shopVisit?.claim_approval_period, "day").isAfter(shopVisit?.shop_visit_end_date)  ? moment(shopVisit?.shop_visit_end_date).add(shopVisit?.claim_approval_period, "day") : null;

  // Engine (asset_type === 2) module claim amounts, sourced from shopVisit.events (so Add mode,
  // which has no claim_events yet, still renders the correct module split), keyed by
  // `${shop_visit_event_id}_${shop_visit_event_module_id}`.
  const nonLlpModules = useMemo(() => {
    if (!Array.isArray(shopVisit?.events)) return [];
    const modules = [];
    const originalClaimEvents = modeType === 'edit' && Array.isArray(claim?.claim_events) ? claim.claim_events : [];
    shopVisit.events.forEach((event) => {
      if (event?.maintenance_program?.is_llp_event === false) {
        const originalEvent = originalClaimEvents.find((ce) => ce?.shop_visit_event_id === event?.id);
        (event?.modules || []).forEach((mod) => {
          const originalModule = originalEvent?.modules?.find((om) => om?.shop_visit_event_module_id === mod?.id);
          modules.push({ ...mod, shop_visit_event_id: event?.id, shop_visit_event_module_id: mod?.id, claim_amount: originalModule?.claim_amount ?? '' });
        });
      }
    });
    return modules;
  }, [shopVisit?.events, claim?.claim_events, modeType]);

  const nonLlpEventsWithoutModules = useMemo(() => {
    if (!Array.isArray(shopVisit?.events)) return [];
    const originalClaimEvents = modeType === 'edit' && Array.isArray(claim?.claim_events) ? claim.claim_events : [];
    return shopVisit.events
      .filter((event) => event?.maintenance_program?.is_llp_event === false && (!event?.modules || event.modules.length === 0))
      .map((event) => {
        const originalEvent = originalClaimEvents.find((ce) => ce?.shop_visit_event_id === event?.id);
        return { ...event, shop_visit_event_id: event?.id, claim_amount: originalEvent?.claim_amount ?? '' };
      });
  }, [shopVisit?.events, claim?.claim_events, modeType]);

  const [moduleClaimAmount, setModuleClaimAmount] = useState(() => {
    const initial = {};
    nonLlpModules.forEach((mod) => {
      initial[`${mod.shop_visit_event_id}_${mod.shop_visit_event_module_id}`] = mod?.claim_amount ?? '';
    });
    return initial;
  });
  const [eventClaimAmount, setEventClaimAmount] = useState(() => {
    const initial = {};
    nonLlpEventsWithoutModules.forEach((event) => {
      initial[event.shop_visit_event_id] = event?.claim_amount ?? '';
    });
    return initial;
  });

  useEffect(() => {
    dispatch(getStorage({ lease_slug: params?.leaseSlug, module_name: "shop_visit_claim", setIsDropDownLoading: setLoading }))
  }, [])

  const onFieldChange = (key, value) => { setClaim({ ...claim, [key]: value }) }
  const onModuleAmountChange = (shopVisitEventId, shopVisitEventModuleId, value) => {
    setModuleClaimAmount({ ...moduleClaimAmount, [`${shopVisitEventId}_${shopVisitEventModuleId}`]: value })
  }
  const onEventAmountChange = (shopVisitEventId, value) => {
    setEventClaimAmount({ ...eventClaimAmount, [shopVisitEventId]: value })
  }
  const onFocusChange = (key) => { setError({ ...error, [key]: "" }) }
  const handleFileChange = (files) => { setUploadedFiles(files); dispatch(setFileUploadDetails({ attachments: files })) }
  const isAfterAllowedDate =claim?.submission_date &&(
    (claimPeriodEndDate && moment(claim.submission_date).isAfter(moment(claimPeriodEndDate))) ||
    (!claimPeriodEndDate && shopVisit?.shop_visit_end_date && moment(claim.submission_date).isAfter(moment(shopVisit.shop_visit_end_date)))
  );

  const onAddEditClaim = async () => {
    const hasNonLlpSplit = nonLlpModules?.length > 0 || nonLlpEventsWithoutModules?.length > 0;
    let validationInputs = {
      submission_date: !claim?.submission_date ? "Please select Submission Date" : !isValidYearRange(claim?.submission_date) ? "Year must be between 1900 and 2100." : "",
      claim_amount: claim?.claim_amount || hasNonLlpSplit || ((isLlpEvent) && (!isPrsv)) ? "" : "Please enter Claim Amount",
      period_extend_reason:isAfterAllowedDate ? claim?.period_extend_reason? "":"Please enter Period Extension Reason":""

    };
    if (moment(leaseBasicDetails?.start_date).isAfter(moment(claim?.submission_date))) {
      return enqueueSnackbar(`Submission Date : Must be between lease start and end dates.`, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
    }
    else if (moment(leaseBasicDetails?.end_date).isBefore(moment(claim?.submission_date))) {
      return enqueueSnackbar(`Submission Date : Must be between lease start and end dates.`, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
    }


    const svCost = Number(shopVisit?.cost) || 0;
    if (claim?.lessor_contribution && Number(claim.lessor_contribution) > svCost) {
      return enqueueSnackbar(`Lessor Contribution must be equal to or less than Shop Visit Cost (Max: ${svCost}).`, { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
    }
    const showClaimError = (prsvAmount, llpAmount) => {
      const parts = [];
      if (prsvAmount) parts.push(`PRSV Claim: ${prsvAmount}`);
      if (llpAmount) parts.push(`LLP Claim: ${llpAmount}`);
      if (prsvAmount && llpAmount) parts.push(`Total: ${Number(prsvAmount) + Number(llpAmount)}`);
      parts.push(`Max: ${svCost}`);
      enqueueSnackbar(`Claim exceeds shop visit limit (${parts.join(', ')}).`, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    };
    if (shopVisit?.asset_type === 2) {
      nonLlpModules.forEach((module) => {
        const fieldKey = `${module.shop_visit_event_id}_${module.shop_visit_event_module_id}`;
        const claimAmt = Number(moduleClaimAmount[fieldKey]) || 0;
        const moduleCost = Number(module?.cost) || 0;
        if (claimAmt > moduleCost) {
          validationInputs[fieldKey] = `${module?.module_name}: Claim amount exceeds shop visit amount by ${claimAmt - moduleCost}`;
        }
      });
      const prsvTotal = Object.values(moduleClaimAmount).reduce((sum, v) => sum + (Number(v) || 0), 0)
        + Object.values(eventClaimAmount).reduce((sum, v) => sum + (Number(v) || 0), 0);
      const llpTotal = isLlpEvent ? Number(llpClaimTotal || 0) : 0;
      if (isPrsv && isLlpEvent) {
        if ((prsvTotal + llpTotal) > svCost) {
          showClaimError(prsvTotal, llpTotal);
          return;
        }
      } else if (isPrsv) {
        if (prsvTotal > svCost) {
          showClaimError(prsvTotal, 0);
          return;
        }
      } else if (isLlpEvent) {
        if (llpTotal > svCost) {
          showClaimError(0, llpTotal);
          return;
        }
      }
    } else if (claim?.claim_amount && Number(claim.claim_amount) > svCost) {
      showClaimError(claim?.claim_amount, 0);
      return;
    }
    if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; })) {
      const updatedPayload = { ...claim, shop_visit_id: shopVisit?.id, claim_amount: claim?.claim_amount ? Number(claim?.claim_amount) : 0, lessor_contribution: claim?.lessor_contribution ? Number(claim?.lessor_contribution) : 0, payout_status: claim?.payout_status?.value, period_extend_reason: isAfterAllowedDate ? claim?.period_extend_reason : "" }
      if (shopVisit?.asset_type === 2) {
        delete updatedPayload.claim_amount;
      }
      delete updatedPayload.previous_approver_users;
      if (Array.isArray(claim?.approver_users)) {
        const approverUserIds = claim.approver_users.map((user) => (user?.id ? user.id : user)).filter((id) => Number(id));
        updatedPayload.approver_users = [...new Set(approverUserIds)];
      }
      if (shopVisit?.asset_type === 2 && Array.isArray(shopVisit?.events)) {
        const originalClaimEvents = modeType === 'edit' && Array.isArray(claim?.claim_events) ? claim.claim_events : [];
        updatedPayload.claim_events = shopVisit.events.map(ev => {
          const originalEvent = originalClaimEvents.find(ce => ce?.shop_visit_event_id === ev?.id);
          if (ev?.maintenance_program?.is_llp_event === true) {
            return {
              ...(originalEvent?.id ? { id: originalEvent.id } : {}),
              shop_visit_event_id: ev?.id,
              claim_amount: Number(llpClaimTotal) || 0,
            };
          }
          if (Array.isArray(ev?.modules) && ev.modules.length > 0) {
            const modules = ev.modules.map(mod => {
              const originalModule = originalEvent?.modules?.find(om => om?.shop_visit_event_module_id === mod?.id);
              return {
                ...(originalModule?.id ? { id: originalModule.id } : {}),
                shop_visit_event_module_id: mod?.id,
                claim_amount: Number(moduleClaimAmount[`${ev.id}_${mod.id}`]) || 0,
              };
            });
            const claim_amount = modules.reduce((sum, m) => sum + (Number(m.claim_amount) || 0), 0);
            return { ...(originalEvent?.id ? { id: originalEvent.id } : {}), shop_visit_event_id: ev?.id, claim_amount, modules };
          }
          return {
            ...(originalEvent?.id ? { id: originalEvent.id } : {}),
            shop_visit_event_id: ev?.id,
            claim_amount: Number(eventClaimAmount[ev.id]) || 0,
          };
        });
      }
      const response = await saveClaim(updatedAsset, updatedPayload);
      if (response) {
        if (uploadedFiles?.length > 0 ) {
          fileUpload(response?.slug, 'updated')
        }
        onToggleModal();
        getResponseBack();
      }
    } else {
      setError(validationInputs)
    }
  }
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Shop Visit Claim`, page_type: page_type, })
  }
  
  return (
    <>
      <Dialog
        aria-labelledby="customized-dialog-title"
        className='add-lease-dialog'
        maxWidth='md'
        fullWidth
        open={addEditClaim?.modal}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header'>
          <Typography sx={{ marginLeft: '10px' }}>
            {modeType === 'add' ? 'Add Claim' : modeType === 'edit' ? 'Edit Claim' : 'View Claim'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ md: 4, xs: 12 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  required
                  label="Submission Date"
                  value={claim?.submission_date ? moment(claim?.submission_date) : null}
                  onChange={(data, value) => onFieldChange('submission_date', moment(data).format(backendDateFormat))}
                  slotProps={{
                    textField: {
                      required: "true",
                      InputLabelProps: { shrink: true },
                      size: "small",
                      margin: "normal",
                      helperText: error?.submission_date,
                      error: error?.submission_date,
                      onFocus: () => { onFocusChange("submission_date") }
                    },
                  }}
                  sx={{ width: "100%" }}
                  format={fieldDateFormat}
                  disabled={modeType === 'view'}
                />
              </LocalizationProvider>
            </Grid>
            {shopVisit?.asset_type !== 2 ? <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                required
                id="claim_amount"
                value={modeType === 'view' && claim?.claim_amount !== '' && claim?.claim_amount !== null && claim?.claim_amount !== undefined ? numberWithCommas(roundAmount(claim?.claim_amount)) : claim?.claim_amount}
                onChange={(e, value) => { regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange('claim_amount', e.target.value) : e.preventDefault() }}
                label="Claim Amount"
                variant="outlined"
                size="small"
                margin="normal"
                error={!!error?.claim_amount}
                helperText={error?.claim_amount}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                onFocus={() => onFocusChange("claim_amount")}
                fullWidth
                disabled={modeType === 'view'}
              />
            </Grid> : null}
            <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                id="lessor_contribution"
                value={modeType === 'view' && claim?.lessor_contribution !== '' && claim?.lessor_contribution !== null && claim?.lessor_contribution !== undefined ? numberWithCommas(roundAmount(claim?.lessor_contribution)) : claim?.lessor_contribution}
                onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange('lessor_contribution', e.target.value) : e.preventDefault() }}
                label="Lessor Contribution"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                onFocus={() => onFocusChange("lessor_contribution")}
                fullWidth
                disabled={modeType === 'view'}
              />
            </Grid>
            {isLlpEvent ?
              <Grid size={{ md: 4, xs: 12 }}>
                <TextField
                  id="name"
                  label="LLP Claim Amount"
                  fullWidth
                  size="small"
                  margin="normal"
                  value={numberWithCommas(roundAmount(llpClaimTotal || 0))}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                  disabled
                />
              </Grid> : null}
            {shopVisit?.asset_type === 2 ?
              <>
                {nonLlpModules?.length > 0 && (
                  <Grid size={{ md: 12, xs: 12 }} sx={{ mt: 1 }}>
                    <Typography className='section-header-text'><u>Engine PRSV Claim Cost Split Details:</u></Typography>
                    <Box className="escalation-container">
                      <Box className="escalation-box">
                        <Box sx={{ display: "flex", flexWrap: "wrap", rowGap: 1.5, columnGap: 3 }}>
                          {nonLlpModules.map((module) => {
                            const fieldKey = `${module.shop_visit_event_id}_${module.shop_visit_event_module_id}`;
                            const rawAmount = moduleClaimAmount[fieldKey];
                            const amount = modeType === 'view' && rawAmount !== '' && rawAmount !== null && rawAmount !== undefined ? numberWithCommas(roundAmount(rawAmount)) : rawAmount;
                            return (
                              <TextField
                                key={fieldKey}
                                id={`${fieldKey}_claim_amount`}
                                label={
                                  module?.is_non_llp ? (
                                    <span style={{ verticalAlign: 'super' }}>
                                      {module?.module_name}
                                      <Tooltip placement='right' title={<span>Non-LLP</span>} arrow>
                                        <InfoIcon fontSize='small' color='primary' style={{ verticalAlign: 'middle', marginLeft: '2px', fontSize: 'medium' }} />
                                      </Tooltip>
                                    </span>
                                  ) : module?.module_name
                                }
                                margin="normal"
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                                value={amount ?? ''}
                                onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) ? onModuleAmountChange(module.shop_visit_event_id, module.shop_visit_event_module_id, e.target.value) : e.preventDefault() }}
                                onFocus={() => onFocusChange(fieldKey)}
                                error={error && error[fieldKey]}
                                helperText={error && error[fieldKey]}
                                size="small"
                                disabled={modeType === 'view'}
                                sx={{ width: 150, flexShrink: 0 }}
                              />
                            )
                          })}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ) }
                {nonLlpEventsWithoutModules.map((event) => {
                  const rawAmount = eventClaimAmount[event.shop_visit_event_id];
                  const amount = modeType === 'view' && rawAmount !== '' && rawAmount !== null && rawAmount !== undefined ? numberWithCommas(roundAmount(rawAmount)) : rawAmount;
                  return (
                    <Grid size={{ md: 4, xs: 12 }} key={event.shop_visit_event_id}>
                      <TextField
                        id={`event_${event.shop_visit_event_id}_claim_amount`}
                        value={amount ?? ''}
                        label={event?.maintenance_program?.name || "Claim Amount"}
                        onChange={(e) => { regexConstants.decimalnodotstart.test(e.target.value) ? onEventAmountChange(event.shop_visit_event_id, e.target.value) : e.preventDefault() }}
                        variant="outlined"
                        onFocus={() => onFocusChange(`event_${event.shop_visit_event_id}`)}
                        size="small"
                        margin="normal"
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                        fullWidth
                        error={error && error[`event_${event.shop_visit_event_id}`]}
                        helperText={error && error[`event_${event.shop_visit_event_id}`]}
                      />
                    </Grid>
                  )
                })}
              </> : null}
              {isAfterAllowedDate && (
              <Grid size={{ md: 12, xs: 12 }}>
                <TextField
                  id="period_extend_reason"
                  value={claim?.period_extend_reason}
                  onChange={(e) => onFieldChange('period_extend_reason', e.target.value.trimStart())}
                  label="Period Extension Reason"
                  variant="outlined"
                  size="small"
                  margin="normal"
                  slotProps={{ inputLabel: { shrink: true } }}
                  onFocus={() => onFocusChange("period_extend_reason")}
                  sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                  fullWidth
                  rows={3}
                  multiline
                  required
                  helperText={`Submission date cannot be greater than claim validity period ${claimPeriodEndDate
                      ? claimPeriodEndDate.format(fieldDateFormat)
                      : moment(shopVisit?.shop_visit_end_date).format(fieldDateFormat)
                    }, kindly add Period Extension Reason to proceed.`}
                  error={!!error?.period_extend_reason}
                  disabled={modeType === 'view'}
                />
              </Grid>
            )}
            <Grid size={{ md: 12, xs: 12 }}>
              <TextField
                id="description"
                value={claim?.description}
                onChange={(e, value) => onFieldChange('description', e.target.value)}
                label="Claim Details"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }}}
                sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                onFocus={() => onFocusChange("description")}
                fullWidth
                rows={3}
                multiline
                disabled={modeType === 'view'}
              />
            </Grid>
            <Grid size={{ md: 12, xs: 12 }}>
              <TextField
                id="remarks"
                value={claim?.remarks}
                onChange={(e, value) => onFieldChange('remarks', e.target.value)}
                label="Remarks"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }}}
                sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                onFocus={() => onFocusChange("remarks")}
                fullWidth
                rows={3}
                multiline
                disabled={modeType === 'view'}
              />
            </Grid>
            <Grid size={{ md: 12, xs: 12 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }} >
                {claim?.id && addEditClaim?.data?.attachments?.length ?
                  <Button onClick={() => { onOpenAttachments(); handleItemClicked('ViewAttachments Button') }} size='small' variant='outlined' color='primary' sx={{ marginBottom: '10px' }}>({addEditClaim?.data?.attachments?.length})Attachment(s)</Button> : null
                }
                <Button className='available-space-btn' disableRipple size='small' variant='outlined' color='primary' endIcon={isLoading?.storage ? <ButtonLoader size={15} /> : null}>{`Available Space : ${isLoading?.storage ? "" : formatBytes(storageDetails?.remaining_storage)}`}</Button>
              </Box>
              {modeType === 'view' ? null : <AttachementsUpload onFileChange={handleFileChange} attachments={addEditClaim?.data?.attachments} remainingStorage={storageDetails?.remaining_storage} />}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className="dialog-action">
          <Button className="cancel-btn" variant='outlined' size='small' disabled={isLoading?.buttonLoader} onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}> Cancel </Button>
          <Button variant='contained' className='btn-primary' disabled={modeType === 'view'} size='small' onClick={() => { onAddEditClaim(); handleItemClicked('Save Button') }}>
            {isLoading?.buttonLoader ? <CircularProgress color='inherit' size={25} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      {isLoading?.pageLoader ? <PageLoader /> : null}
    </>

  )
}

export default ClaimCRU